import { useRef, useState } from 'react';
import { PageHero } from '@/components/PageHero';
import { PageLayout } from '@/components/PageLayout';
import { Seo } from '@/components/Seo';
import { ROUTES } from '@/constants/site';
import { ContactForm, FIELDS } from './ContactForm';
import { PrivacyModal } from './PrivacyModal';
import { PrivacyNotice } from './PrivacyNotice';

const DESCRIPTION =
  'Nova 플랫폼 도입 문의. 문의를 남겨주시면 운영팀 확인 후 영업일 기준 3~4일 내에 회신드립니다.';

const FORM_ID = 'contactForm';
const REQUIRED_FIELDS = FIELDS.filter((field) => field.required);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InquiryPage() {
  // 입력값은 DOM 이 들고 있는다 — 타이핑마다 리렌더할 이유가 없고,
  // 검증은 제출 시점에 한 번 훑는다
  const fieldRefs = useRef(
    Object.fromEntries(FIELDS.map((field) => [field.id, { current: null }])),
  ).current;

  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  const [agreeThird, setAgreeThird] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const agreedToRequired = agreeThird && agreePrivacy;

  const validate = () => {
    const next = {};
    let firstInvalid = null;

    REQUIRED_FIELDS.forEach((field) => {
      const el = fieldRefs[field.id].current;
      const raw = el?.value ?? '';
      const empty = !raw.trim();
      const badEmail = field.type === 'email' && !empty && !EMAIL_PATTERN.test(raw);

      if (empty || badEmail) {
        next[field.id] = badEmail
          ? '* 올바른 이메일 주소를 입력해주세요.'
          : field.emptyMessage;
        if (!firstInvalid) firstInvalid = el;
      }
    });

    setErrors(next);
    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }
    return true;
  };

  const submitInquiry = () => {
    // TODO: 실제 접수 API 연동
    setBusy(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate() || !agreedToRequired) return;
    submitInquiry();
  };

  const handleAgreeAll = () => {
    setAgreeThird(true);
    setAgreeMarketing(true);
    setAgreePrivacy(true);
    // validate 는 DOM 값을 직접 읽으므로 위 setState 반영을 기다릴 필요가 없다
    if (validate()) submitInquiry();
  };

  const handleFieldInput = (id) => {
    if (!fieldRefs[id].current?.value.trim()) return;
    setErrors((prev) => (prev[id] ? { ...prev, [id]: undefined } : prev));
  };

  return (
    <>
      <Seo
        title="플랫폼 도입문의"
        ogTitle="플랫폼 도입문의 | Nova"
        description={DESCRIPTION}
        path={ROUTES.inquiry}
      />
      <PageLayout bodyClass="page-inquiry">
        <PageHero title="플랫폼 도입문의">
          문의를 남겨주시면
          <br />
          운영팀 확인 후 영업일 기준 3~4일 내에 회신드립니다.
        </PageHero>

        <section id="contact" aria-labelledby="contact-title" className="bg-white py-20">
          <div className="inner">
            <header className="mb-10">
              <h2 id="contact-title" className="plan-title headline-stack">
                <span className="heading-line">신청서 작성</span>
              </h2>
              <p className="section-subtitle mt-5">
                아래 항목을 작성해주시면 운영팀에서 확인 후 답변드립니다.
              </p>
            </header>

            <ContactForm
              formId={FORM_ID}
              fieldRefs={fieldRefs}
              errors={errors}
              busy={busy}
              onFieldInput={handleFieldInput}
              onSubmit={handleSubmit}
              agreeThird={agreeThird}
              agreeMarketing={agreeMarketing}
              onAgreeThirdChange={setAgreeThird}
              onAgreeMarketingChange={setAgreeMarketing}
              onOpenPrivacyModal={() => setPrivacyModalOpen(true)}
            />
          </div>
        </section>

        <PrivacyNotice
          formId={FORM_ID}
          agreePrivacy={agreePrivacy}
          onAgreePrivacyChange={setAgreePrivacy}
          submitDisabled={!agreedToRequired}
          onAgreeAll={handleAgreeAll}
        />

        <PrivacyModal
          open={privacyModalOpen}
          onClose={() => setPrivacyModalOpen(false)}
        />
      </PageLayout>
    </>
  );
}
